/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toActivityField } from '@data/dto/activity-field.dto';
import { toSortParams } from '@data/dto/api.dto';
import {
  ActivityField,
  ActivityFieldHttp,
  ActivityFieldWizard,
} from '@data/models/activity-field.interface';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class ActivityFieldService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly activityFieldEndpoint = '/activity-field-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listActivityFields(
    filters: FilterParams,
  ): Promise<PaginateResult<ActivityField[]>> {
    const { data: response, error } = await this.API?.get<{
      data: ActivityFieldHttp[];
      meta: Meta;
    }>(this.activityFieldEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toActivityField) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertActivityField({
    id,
    ...activityFieldWizard
  }: ActivityFieldWizard): Promise<ActivityField | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<ActivityFieldWizard, 'id'> },
        { data: ActivityFieldHttp }
      >(this.activityFieldEndpoint, { data: activityFieldWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toActivityField(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: ActivityFieldWizard },
      { data: ActivityFieldHttp }
    >(`${this.activityFieldEndpoint}/${id}`, {
      data: { ...activityFieldWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toActivityField(data?.data || {});
  }

  public async deleteActivityField(id: string): Promise<ActivityField | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: ActivityFieldHttp;
    }>(`${this.activityFieldEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toActivityField(data?.data || {});
  }

  public async sortActivityField(
    sortParams: DatatableReorderParams<ActivityField>,
  ): Promise<ActivityField | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.activityFieldEndpoint}/${id}/sort`,
      {
        data: toSortParams('activity_field_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
